import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { slugify } from "../state/definition";
import type { SimulationDef } from "../types";

interface LibraryDrawerProps {
  readOnly?: boolean;
  definition: SimulationDef;
  definitionName: string | null;
  dirty: boolean;
  onOpen: (raw: Record<string, unknown>, name: string | null) => void;
  onClose: () => void;
  onToast: (text: string) => void;
}

export function LibraryDrawer({
  readOnly = false,
  definition,
  definitionName,
  dirty,
  onOpen,
  onClose,
  onToast,
}: LibraryDrawerProps) {
  const [scenarios, setScenarios] = useState<string[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [saveName, setSaveName] = useState(definitionName ?? slugify(definition.name));

  const refresh = useCallback(() => {
    api
      .scenarios()
      .then((list) => setScenarios(list.map((s) => s.name)))
      .catch((error: unknown) => onToast(String(error)));
    // The hosted snapshot API has no workspace — skip the definitions fetch.
    if (readOnly) return;
    api
      .definitions()
      .then((list) => setSaved(list.map((d) => d.name)))
      .catch((error: unknown) => onToast(String(error)));
  }, [onToast, readOnly]);

  useEffect(refresh, [refresh]);

  const confirmDiscard = () =>
    !dirty || window.confirm("Discard unsaved changes to the current definition?");

  const openScenario = (name: string) => {
    if (!confirmDiscard()) return;
    // Shipped examples are read-only: opening one yields an unsaved copy.
    api
      .scenario(name)
      .then((raw) => onOpen(raw, null))
      .catch((error: unknown) => onToast(String(error)));
  };
  const openSaved = (name: string) => {
    if (!confirmDiscard()) return;
    api
      .definition(name)
      .then((raw) => onOpen(raw, name))
      .catch((error: unknown) => onToast(String(error)));
  };
  const save = () => {
    const name = slugify(saveName);
    api
      .saveDefinition(name, definition)
      .then(() => {
        onToast(`saved ${name}`);
        // Saving adopts the workspace identity (clears the dirty marker).
        onOpen(definition as unknown as Record<string, unknown>, name);
      })
      .catch((error: unknown) => onToast(String(error)));
  };
  const remove = (name: string) => {
    api
      .deleteDefinition(name)
      .then(() => {
        onToast(`deleted ${name}`);
        refresh();
      })
      .catch((error: unknown) => {
        onToast(String(error));
        refresh(); // the item may already be gone — re-sync the list
      });
  };

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer" aria-label="library">
        <h3>shipped scenarios — read-only</h3>
        <div className="drawer-list">
          {scenarios.map((name) => (
            <div key={name} className="drawer-item">
              <button onClick={() => openScenario(name)} title="open a copy">
                {name}
              </button>
            </div>
          ))}
        </div>
        {!readOnly && (
          <>
            <h3>workspace definitions</h3>
            <div className="drawer-list">
              {saved.length === 0 && <span className="history-empty">nothing saved yet</span>}
              {saved.map((name) => (
                <div key={name} className="drawer-item">
                  <button onClick={() => openSaved(name)}>{name}</button>
                  <button className="row-remove" title="delete" onClick={() => remove(name)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            <h3>save current as…</h3>
            <div className="drawer-save">
              <input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                aria-label="save name"
              />
              <button className="btn" onClick={save}>
                Save
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
